// How a user sets the crate forecast up for a site is a per-site habit: one site
// predicts its rejects and another only its washing, one site's planner works two of
// its six crate types. Users were redoing the same setup every time they came back to
// a site, so both choices are remembered against the site id.
//
// localStorage rather than the sessionStorage the plant selection uses (see
// helpers/plants): the plant being consulted is a per-tab thing, but how a site is
// forecast should still be set the way they left it tomorrow morning.
export const FORECAST_MEANS_KEY = 'crateForecastMeans'
export const FORECAST_HIDDEN_TYPES_KEY = 'crateForecastHiddenTypes'

// The request parameters behind the Means dropdown, in the order they are offered.
export const MEAN_FLAG_NAMES = ['collect', 'washing', 'repair', 'reject', 'delivery']

function readMap (key) {
  try {
    const stored = JSON.parse(localStorage.getItem(key))
    return stored && typeof stored === 'object' ? stored : {}
  } catch (error) {
    return {}
  }
}

function readEntry (key, plantId) {
  if (plantId === null || plantId === undefined) {
    return null
  }
  const entry = readMap(key)[String(plantId)]
  return entry === undefined ? null : entry
}

function writeEntry (key, plantId, value) {
  if (plantId === null || plantId === undefined) {
    return
  }
  const all = readMap(key)
  all[String(plantId)] = value
  try {
    localStorage.setItem(key, JSON.stringify(all))
  } catch (error) {
    // Storage full, or blocked by the browser. Not worth interrupting a forecast for.
  }
}

// Null for a site nothing was ever saved for, so the caller can leave the flags alone
// instead of reading missing keys as "every mean off".
export function restoreMeanFlags (plantId) {
  const saved = readEntry(FORECAST_MEANS_KEY, plantId)
  if (!saved || typeof saved !== 'object') {
    return null
  }
  const flags = {}
  MEAN_FLAG_NAMES.forEach((name) => {
    if (typeof saved[name] === 'boolean') {
      flags[name] = saved[name]
    }
  })
  return Object.keys(flags).length > 0 ? flags : null
}

export function saveMeanFlags (plantId, flags) {
  if (!flags) {
    return
  }
  const kept = {}
  MEAN_FLAG_NAMES.forEach((name) => {
    if (typeof flags[name] === 'boolean') {
      kept[name] = flags[name]
    }
  })
  writeEntry(FORECAST_MEANS_KEY, plantId, kept)
}

// The types put away rather than the ones on screen: stored this way, a crate type
// added to the site's config later shows up on its own instead of staying hidden
// behind a choice made before it existed, and a retired one just drops out.
export function restoreHiddenTypes (plantId) {
  const saved = readEntry(FORECAST_HIDDEN_TYPES_KEY, plantId)
  if (!Array.isArray(saved)) {
    return null
  }
  return saved.filter(name => typeof name === 'string')
}

export function saveHiddenTypes (plantId, typeNames) {
  if (!Array.isArray(typeNames)) {
    return
  }
  writeEntry(FORECAST_HIDDEN_TYPES_KEY, plantId, typeNames.filter(name => typeof name === 'string'))
}
